import { ServerError } from '@azure/msal-browser'
import { handleErrorResponse } from '@/configs/client-error-handlers'
import { useAuthStore } from '@/shared/stores/AuthStore'
import { useToast } from '@/shared/composables/UseToast'

/**
 * Source: https://orval.dev/reference/configuration/output#usedates
 * Related issue: https://github.com/orval-labs/orval/issues/1939
 * Typed fetcher for Orval-generated API functions.
 *
 * This function appends the Authorization header (if available) and parses the response as JSON.
 * Use as the `mutator`/`fetcher` in orval.config.ts for type-safe, authenticated API calls.
 *
 * @example
 *   const data = await orvalFetcher<MyResponseType>(url, options)
 */

const isoDateFormat
  = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/

function isIsoDateString(value: any): boolean {
  return value && typeof value === 'string' && isoDateFormat.test(value)
}

/**
 * Recursively converts ISO date strings to Date objects in the response data.
 * This is required when using orval with `useDates: true` and the fetch client.
 *
 * @param body The response data to process
 * @returns The data with ISO date strings converted to Date objects
 */
export function handleDates(body: any): any {
  if (body === null || body === undefined || typeof body !== 'object')
    return body

  for (const key of Object.keys(body)) {
    const value = body[key]
    if (isIsoDateString(value)) {
      body[key] = new Date(value) // Convert ISO string to Date object
    } else if (typeof value === 'object') {
      handleDates(value) // Recursively handle nested objects
    }
  }

  return body
}

const BASE_URL = '/api'

export function createFetchWithAuth(baseUrl: string) {
  return async function <T>(
    url: string,
    options: RequestInit = {},
  ): Promise<T> {
    const fullUrl = `${baseUrl}${url}`
    const headers = await buildHeadersWithAuth(options)

    let response: Response

    try {
      response = await fetch(fullUrl, {
        ...options,
        headers,
      })
    } catch (error: Error | any) {
      if (error.name === 'AbortError') {
        // Don't catch abort errors, let them propagate
        throw error
      }

      const toast = useToast()

      toast.error('Het lijkt erop dat de server niet bereikbaar is. Probeer het later opnieuw.')

      return Promise.reject(new ServerError('Server not reachable', error))
    }

    if (!response.ok) {
      return await handleErrorResponse(options, response, useToast())
    }

    let data: any = undefined
    const contentLength = response.headers.get('content-length')
    const contentType = response.headers.get('content-type')

    // Only try to parse JSON if there is a body and it's JSON
    if (
      response.status !== 204 // 204 No Content
      && contentLength !== '0'
      && contentType
      && contentType.includes('application/json')
    ) {
      try {
        data = await response.json()
        // Convert ISO date strings to Date objects
        handleDates(data)
      } catch {
        data = null
      }
    }

    return data as T
  }
}

/**
 * A typed fetcher for Orval-generated API functions.
 *
 * This function appends the Authorization header (if available) and parses the response as JSON.
 * Use as the `mutator`/`fetcher` in orval.config.ts for type-safe, authenticated API calls.
 *
 * @param url The URL to fetch.
 * @param options The options to pass to the fetch function.
 * @returns The response data.
 */
const fetcher = createFetchWithAuth(BASE_URL)

/**
 * A typed fetcher for Orval-generated API functions.
 *
 * This function appends the Authorization header (if available) and parses the response as JSON.
 * Use as the `mutator`/`fetcher` in orval.config.ts for type-safe, authenticated API calls.
 *
 * @param url The URL to fetch.
 * @param options The options to pass to the fetch function.
 * @returns The response data.
 */
export async function fetchWithAuth<T>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  return fetcher(url, options)
}

async function buildHeadersWithAuth(options: RequestInit): Promise<Headers> {
  const accessToken = await useAuthStore().getAccessToken?.()

  const headers = new Headers(options.headers || {})
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }

  return headers
}
